import { SMS_DEFAULT_TEMPLATES } from './smsDefaultTemplates';

export const SMS_LANGUAGES = [
    { code: 'fr-fr', label: 'FR' },
    { code: 'en-us', label: 'EN' },
    { code: 'de-de', label: 'DE' },
    { code: 'nl-nl', label: 'NL' },
];

export const SMS_LANGUAGE_CODE_PATTERN = /^[a-z]{2}-[a-z]{2}$/;

export const normalizeSmsLanguageCode = (code) => (
    (code || '').trim().replace(/_/g, '-').toLowerCase()
);

export const isValidSmsLanguageCode = (code) => (
    SMS_LANGUAGE_CODE_PATTERN.test(normalizeSmsLanguageCode(code))
);

export const getSmsLanguageLabel = (code) => {
    const normalized = normalizeSmsLanguageCode(code);
    const known = SMS_LANGUAGES.find((lang) => lang.code === normalized);
    return known?.label || normalized.toUpperCase();
};

export const deriveExtraLanguagesFromMessages = (smsMessages) => {
    const defaultCodes = new Set(SMS_LANGUAGES.map(({ code }) => code));
    const result = {};
    SMS_MESSAGE_PROCESS_KEYS.forEach((processKey) => {
        const extras = Object.keys(smsMessages?.[processKey] || {})
            .map(normalizeSmsLanguageCode)
            .filter((code) => code && !defaultCodes.has(code));
        if (extras.length > 0) {
            result[processKey] = [...new Set(extras)];
        }
    });
    return result;
};

export const getLanguageCodesForProcess = (smsMessages, processKey, extraLanguages = []) => {
    const defaultCodes = SMS_LANGUAGES.map(({ code }) => code);
    const fromMessages = Object.keys(smsMessages?.[processKey] || {})
        .map(normalizeSmsLanguageCode)
        .filter((code) => code && !defaultCodes.includes(code));
    const mergedExtras = [...extraLanguages, ...fromMessages]
        .map(normalizeSmsLanguageCode)
        .filter((code) => code && !defaultCodes.includes(code));
    const uniqueExtras = [...new Set(mergedExtras)].sort();
    return [...defaultCodes, ...uniqueExtras];
};

export const getAllLanguageCodesFromMessages = (smsMessages, processKey) => (
    getLanguageCodesForProcess(smsMessages, processKey, [])
);

export const SMS_MESSAGE_PROCESS_KEYS = [
    'triggered_sms',
    'bank_account_details_reminder_sms',
];

export const SMS_PROCESS_LABELS = {
    no_sms: 'Aucun SMS',
    triggered_sms: 'SMS de déclenchement garantie météo',
    bank_account_details_reminder_sms: 'SMS de relance RIB',
};

export const SMS_PROCESS_PRESETS = [
    { id: 'no_sms', label: SMS_PROCESS_LABELS.no_sms, value: ['no_sms'] },
    { id: 'triggered_sms', label: SMS_PROCESS_LABELS.triggered_sms, value: ['triggered_sms'] },
    {
        id: 'bank_account_details_reminder_sms',
        label: SMS_PROCESS_LABELS.bank_account_details_reminder_sms,
        value: ['bank_account_details_reminder_sms'],
    },
    {
        id: 'triggered_and_bank_reminder',
        label: `${SMS_PROCESS_LABELS.triggered_sms} + ${SMS_PROCESS_LABELS.bank_account_details_reminder_sms}`,
        value: ['triggered_sms', 'bank_account_details_reminder_sms'],
    },
];

/** @deprecated Utiliser SMS_PROCESS_PRESETS */
export const SMS_PROCESS_CHECKBOXES = SMS_PROCESS_PRESETS.filter((p) => p.id !== 'triggered_and_bank_reminder');

/** @deprecated Utiliser SMS_PROCESS_PRESETS */
export const SMS_PROCESS_OPTIONS = [
    ...SMS_PROCESS_PRESETS.slice(0, 3),
    {
        id: 'bank_account_reminder',
        label: SMS_PROCESS_LABELS.bank_account_details_reminder_sms,
        value: ['bank_account_details_reminder_sms'],
    },
    {
        id: 'bank_account_reminder_triggered',
        label: `${SMS_PROCESS_LABELS.bank_account_details_reminder_sms} + ${SMS_PROCESS_LABELS.triggered_sms}`,
        value: ['bank_account_details_reminder_sms', 'triggered_sms'],
    },
];

export const SMS_MESSAGE_SECTION_LABELS = {
    triggered_sms: 'SMS déclenchement (triggered_sms)',
    bank_account_details_reminder_sms: 'SMS relance RIB (bank_account_details_reminder_sms)',
};

export const SMS_MAX_CHARS = 320;
export const SMS_SEGMENT_CHARS = 160;

export const parseSmsManagementResponse = (response) => {
    if (!response) {
        return { smsManagement: null, user: null };
    }

    const payload = response?.data?.sms_management
        ? response.data
        : response;

    if (payload?.sms_management) {
        return {
            smsManagement: payload.sms_management,
            user: payload.user || null,
        };
    }

    if (payload?.sms_management_id || Array.isArray(payload?.sms_processes)) {
        return {
            smsManagement: payload,
            user: payload.user || null,
        };
    }

    return { smsManagement: null, user: null };
};

const normalizeProcessesKey = (processes) => {
    if (!Array.isArray(processes)) return '';
    return [...processes].sort().join(',');
};

export const getSmsProcessOptionById = (id) => (
    SMS_PROCESS_PRESETS.find((option) => option.id === id)
    || SMS_PROCESS_OPTIONS.find((option) => option.id === id)
);

export const getSmsProcessOptionId = (processes) => {
    const key = normalizeProcessesKey(processes);
    const match = SMS_PROCESS_PRESETS.find((option) => normalizeProcessesKey(option.value) === key);
    if (match) return match.id;
    const legacyMatch = SMS_PROCESS_OPTIONS.find((option) => normalizeProcessesKey(option.value) === key);
    return legacyMatch?.id || '';
};

export const getProcessesFromPresetId = (presetId) => {
    const preset = getSmsProcessOptionById(presetId);
    return preset?.value ? [...preset.value] : ['no_sms'];
};

export const getSmsProcessLabel = (processes) => {
    const badges = getSmsProcessBadges(processes);
    return badges.join(' + ');
};

export const getSmsProcessBadges = (processes) => {
    if (!Array.isArray(processes) || processes.length === 0) return ['N/A'];
    if (processes.includes('no_sms')) return [SMS_PROCESS_LABELS.no_sms];
    return processes.map((p) => SMS_PROCESS_LABELS[p] || p);
};

export const normalizeProcessesFromApi = (processes) => {
    if (!Array.isArray(processes) || processes.length === 0) return ['no_sms'];
    if (processes.includes('no_sms')) return ['no_sms'];
    return processes.filter((p) => p !== 'no_sms');
};

export const toggleProcessSelection = (current, processKey) => {
    const list = Array.isArray(current) ? [...current] : [];

    if (processKey === 'no_sms') {
        return ['no_sms'];
    }

    const withoutNoSms = list.filter((p) => p !== 'no_sms');

    if (withoutNoSms.includes(processKey)) {
        const next = withoutNoSms.filter((p) => p !== processKey);
        return next.length === 0 ? ['no_sms'] : next;
    }

    return [...withoutNoSms, processKey];
};

/** Indique si la sélection actuelle correspond à « aucun envoi SMS » */
export const isNoSmsOnly = (processes) => {
    return Array.isArray(processes) && processes.length === 1 && processes[0] === 'no_sms';
};

export const normalizeSmsMessagesFromApi = (smsMessages) => {
    if (!smsMessages || typeof smsMessages !== 'object') return {};
    const result = {};
    SMS_MESSAGE_PROCESS_KEYS.forEach((key) => {
        if (smsMessages[key] && typeof smsMessages[key] === 'object') {
            result[key] = { ...smsMessages[key] };
        }
    });
    return result;
};

export const pruneSmsMessagesForProcesses = (smsMessages, activeProcesses) => {
    if (!smsMessages || activeProcesses.includes('no_sms')) return {};
    const result = {};
    SMS_MESSAGE_PROCESS_KEYS.forEach((key) => {
        if (activeProcesses.includes(key) && smsMessages[key]) {
            result[key] = { ...smsMessages[key] };
        }
    });
    return result;
};

export const sanitizeSmsMessagesForPayload = (smsMessages, activeProcesses) => {
    if (!smsMessages || activeProcesses.includes('no_sms')) return null;

    const result = {};
    activeProcesses.forEach((processKey) => {
        if (!SMS_MESSAGE_PROCESS_KEYS.includes(processKey)) return;
        const langs = smsMessages[processKey] || {};
        const cleaned = {};
        Object.keys(langs).forEach((langKey) => {
            const code = normalizeSmsLanguageCode(langKey);
            const text = (langs[langKey] || '').trim();
            if (text) cleaned[code] = text;
        });
        if (Object.keys(cleaned).length > 0) {
            result[processKey] = cleaned;
        }
    });

    return Object.keys(result).length > 0 ? result : null;
};

export const hasCompleteSmsMessages = (smsMessages, activeProcesses) => {
    if (activeProcesses.includes('no_sms')) return true;
    return activeProcesses.every((processKey) => {
        if (!SMS_MESSAGE_PROCESS_KEYS.includes(processKey)) return true;
        const block = smsMessages?.[processKey];
        if (!block) return true;
        return SMS_LANGUAGES.some(({ code }) => (block[code] || '').trim());
    });
};

export const hasCustomSmsMessages = (smsMessages) => {
    if (!smsMessages || typeof smsMessages !== 'object') return false;
    return SMS_MESSAGE_PROCESS_KEYS.some((key) => {
        const langs = smsMessages[key];
        return langs && Object.values(langs).some((v) => (v || '').trim());
    });
};

export const isValidSmsProcessesCombination = (processes) => {
    if (!Array.isArray(processes) || processes.length === 0) return false;
    if (processes.includes('no_sms')) return processes.length === 1;
    const allowed = new Set(SMS_MESSAGE_PROCESS_KEYS);
    return processes.every((p) => allowed.has(p));
};

export const validateSmsForm = (smsProcesses, smsMessages) => {
    const errors = [];

    if (!isValidSmsProcessesCombination(smsProcesses)) {
        errors.push('Combinaison de processus SMS invalide. « Aucun SMS » ne peut pas être combiné avec d\'autres processus.');
        return errors;
    }

    if (smsProcesses.includes('no_sms')) {
        return errors;
    }

    const prunedMessages = smsMessages && typeof smsMessages === 'object' ? smsMessages : {};

    Object.keys(prunedMessages).forEach((processKey) => {
        if (!SMS_MESSAGE_PROCESS_KEYS.includes(processKey)) {
            errors.push(`Processus invalide dans les messages : ${processKey}`);
            return;
        }
        if (!smsProcesses.includes(processKey)) {
            errors.push(`Le processus « ${SMS_PROCESS_LABELS[processKey] || processKey} » a des messages mais n'est pas activé.`);
        }
        const langs = prunedMessages[processKey];
        if (langs && typeof langs === 'object') {
            Object.keys(langs).forEach((lang) => {
                const code = normalizeSmsLanguageCode(lang);
                if (!isValidSmsLanguageCode(code)) {
                    errors.push(`Code langue invalide : ${lang}. Utilisez le format xx-xx (ex. it-it, pt-br).`);
                    return;
                }
                const text = langs[lang] || '';
                if (text.length > SMS_MAX_CHARS) {
                    errors.push(`Message trop long pour ${processKey} (${code}) : max ${SMS_MAX_CHARS} caractères.`);
                }
            });
        }
    });

    return errors;
};

export const buildSmsManagementPayload = (smsProcesses, smsMessages, { isUpdate = false } = {}) => {
    const processes = smsProcesses.includes('no_sms') ? ['no_sms'] : [...smsProcesses].sort();
    const payload = { sms_processes: processes };

    if (processes.includes('no_sms')) {
        if (isUpdate) {
            payload.sms_messages = null;
        }
        return payload;
    }

    const prunedMessages = pruneSmsMessagesForProcesses(smsMessages, processes);
    payload.sms_messages = sanitizeSmsMessagesForPayload(prunedMessages, processes);
    return payload;
};

export const mergeTemplateIntoMessages = (smsMessages, brand, activeProcesses) => {
    const templates = SMS_DEFAULT_TEMPLATES[brand];
    if (!templates) return smsMessages;

    const next = { ...smsMessages };
    SMS_MESSAGE_PROCESS_KEYS.forEach((processKey) => {
        if (!activeProcesses.includes(processKey) || !templates[processKey]) return;
        next[processKey] = {
            ...(next[processKey] || {}),
            ...templates[processKey],
        };
    });
    return next;
};
